import { setFilterInitialized, setIsUpdatingFilters } from "../builder/tableBuilder.js";
import { hoursToTime } from "./timeUtils.js";

let keepFiltersOnNextUpdate = true;
let splitValueFields = new Set();
let pinnedFilterLabels = {};
let suppressPinInvalidation = false;

export function setSplitFields(fields = []) {
    splitValueFields = new Set(fields);
}

export function setSuppressPinInvalidation(value) {
    suppressPinInvalidation = value;
}

export function discardFiltersOnNextUpdate() {
    keepFiltersOnNextUpdate = false;
}

export function setPinnedFilterLabels(labels = {}) {
    pinnedFilterLabels = labels;
}

function applyPinnedLabelDisplays(table) {
    Object.entries(pinnedFilterLabels).forEach(([field, { label }]) => {
        const input = table.getColumn(field)?.getElement()?.querySelector(".tabulator-header-filter input");
        if (input && input.value !== label) input.value = label;
    });
}

export function updateFilterOptions(visibleRows, table) {
    for (const field of Object.keys(pinnedFilterLabels)) {
        const currentValue = table.getHeaderFilters().find(f => f.field === field)?.value;
        const isCleared = currentValue === undefined || currentValue === '';
        const isMismatched = currentValue !== undefined && String(currentValue) !== String(pinnedFilterLabels[field].value);
        if ((isCleared && !suppressPinInvalidation) || isMismatched) {
            delete pinnedFilterLabels[field];
        }
    }

    if (!visibleRows || visibleRows.length === 0) {
        if (!keepFiltersOnNextUpdate) {
            keepFiltersOnNextUpdate = true;
            table.clearHeaderFilter();
        }
        applyPinnedLabelDisplays(table);
        return;
    }

    const keepFilters = keepFiltersOnNextUpdate;
    keepFiltersOnNextUpdate = true;

    const targetFields = [...new Set(
        Object.keys(visibleRows[0]).filter(key => key.endsWith("Name") || key === "workedHours").map(key => key.replace("Name", ""))
    )];

    const result = {};
    targetFields.forEach(field => {
        const seen = new Set();

        if (splitValueFields.has(field)) {
            result[field] = visibleRows.flatMap(row => {
                const ids = String(row[`${field}Id`] ?? "").split(";").map(s => s.trim()).filter(Boolean);
                const names = String(row[`${field}Name`] ?? "").split(";").map(s => s.trim()).filter(Boolean);
                return ids.map((id, i) => ({ [`${field}Id`]: id, [`${field}Name`]: names[i] ?? id }));
            }).filter(item => {
                const val = item[`${field}Id`];
                if (!val || seen.has(val)) return false;
                seen.add(val);
                return true;
            });
            return;
        }

        result[field] = visibleRows
            .map(row => (field === "workedHours"
                ? { workedHours: row.workedHours }
                : { [`${field}Id`]: row[`${field}Id`], [`${field}Name`]: row[`${field}Name`] }
            ))
            .filter(item => {
                const val = field === "workedHours" ? item.workedHours : item[`${field}Id`];
                if (val === undefined || val === null || val === "" || seen.has(val)) return false;
                seen.add(val);
                return true;
            });

        if (field !== "workedHours") {
            const hasUnknown = visibleRows.some(row => row[`${field}Id`] === "" || row[`${field}Id`] == null);
            if (hasUnknown) {
                result[field].push({ [`${field}Id`]: "__unknown__", [`${field}Name`]: "Unknown" });
            }
        }
    });

    const previousFilters = {};
    if (keepFilters) {
        table.getColumns().forEach(column => {
            const field = column.getField();
            const filterValue = table.getHeaderFilters().find(f => f.field === field)?.value;
            if (filterValue) previousFilters[field] = filterValue;
        });
    }

    setIsUpdatingFilters(true);
    const newColumns = table.getColumns().map(column => {
        const definition = column.getDefinition();
        const field = definition.field;
        const key = field === "workedHours" ? "workedHours" : field?.replace("Id", "");

        definition.visible = column.isVisible();

        if (result[key] && definition.headerFilter !== "input") {
            const values = result[key].map(item => (key === "workedHours"
                ? { label: hoursToTime(item.workedHours), value: item.workedHours }
                : { label: item[`${key}Name`], value: item[`${key}Id`] }
            ));

            if (pinnedFilterLabels[field] && values.some(v => String(v.value) === String(pinnedFilterLabels[field].value))) {
                delete pinnedFilterLabels[field];
            }

            definition.headerFilterParams = { values };

            if (splitValueFields.has(key)) {
                definition.headerFilterFunc = (headerValue, rowValue) => {
                    if (!rowValue) return false;
                    return String(rowValue).split(";").map(s => s.trim()).filter(Boolean).includes(String(headerValue));
                };
            } else if (key !== "workedHours") {
                definition.headerFilterFunc = (headerValue, rowValue) => {
                    if (headerValue === "__unknown__") {
                        return rowValue === "" || rowValue === null || rowValue === undefined;
                    }
                    return rowValue === headerValue;
                };
            }
        }
        return definition;
    });

    table.setColumns(newColumns);
    if (!keepFilters) table.clearHeaderFilter();
    setIsUpdatingFilters(false);
    setFilterInitialized(false);
    Object.entries(previousFilters).forEach(([field, value]) => { table.setHeaderFilterValue(field, value) });
    table.refreshFilter();
    setFilterInitialized(true);

    applyPinnedLabelDisplays(table);
}
